import type { StatementBlockKey, StatementPeriod } from '../types/dealerFundStatementPeriod.types';
import { formatVerbatimMoneyCurrency } from '@shared/utils/formatMoneyDisplay';
import { shortId } from './shortId';

const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

const monthName = (month: string): string => `${MONTH_NAMES[Number(month.slice(5, 7)) - 1]} ${month.slice(0, 4)}`;

const longDate = (iso: string): string => `${iso.slice(8, 10)} ${MONTH_NAMES[Number(iso.slice(5, 7)) - 1]} ${iso.slice(0, 4)}`;

export const formatStatementDates = (from: string, to: string): string => `${longDate(from)} to ${longDate(to)}`;

export const describePeriod = (period: StatementPeriod, todayIso: string): string => {
    if (period.kind === 'range') return formatStatementDates(period.from, period.to);
    return period.month === todayIso.slice(0, 7) ? `${monthName(period.month)} to date` : monthName(period.month);
};

export const monthOptionLabel = (month: string, todayIso: string): string =>
    month === todayIso.slice(0, 7) ? `${monthName(month)} (to date)` : monthName(month);

export const summaryLine = (dealerCode: string | null, includesOwnedDealers: boolean): string => {
    if (!dealerCode) return 'Summary for this dealer, all products';
    return includesOwnedDealers
        ? `Summary for ${dealerCode} and the dealers it owns, all products`
        : `Summary for ${dealerCode}, all products`;
};

export const unverifiedMessage = (periodLabel: string): string =>
    `The ledger could not verify this statement for ${periodLabel}. If this persists after a retry, the period holds movements artemis has not journaled and needs Finance support; retrying will not change it.`;

export const unavailableMessage = (periodLabel: string): string =>
    `The ledger is unavailable, so the fund statement for ${periodLabel} could not be loaded.`;

export const NOT_ENTITLED_MESSAGE = 'The fund statement is available only for the dealers you belong to.';

export const roundingFootnote = (roundingMode: string): string =>
    `Each line is rounded to the penny on its own (${roundingMode.replace(/_/g, '-')}), so the lines may differ from the balances by a few pence.`;

export const UNMAPPED_LABEL = 'Unmapped';
export const NOT_INDEXED_LABEL = 'Not yet indexed';
export const NOT_INDEXED_TOOLTIP =
    "The ledger sync has not created this fund account's set yet, so its statement cannot be read.";
export const NO_FUND_ACCOUNTS_MESSAGE = 'No Client Fund Accounts had movements or members in this period';
export const NO_FUND_ACCOUNT_MATCHES_MESSAGE = 'No Client Fund Accounts match your search';

export const productLabel = (
    productId: string,
    productNames: Map<string, string>,
    sageAccountName: string | null,
): string => productNames.get(productId.toLowerCase()) ?? sageAccountName ?? shortId(productId);

export const fundAccountsHeading = (count: number): string => `Client Fund Accounts \u00b7 ${count} in this summary`;

export const selectPrompt = (periodLabel: string): string =>
    `Select a Client Fund Account to see its statement for ${periodLabel}`;

export const fundAccountCardTitle = (account: {
    sageAccountCode: string | null;
    dealerCode: string | null;
    sellingDealerId: string;
    productLabel: string;
}): string =>
    [
        'Fund Statement',
        account.sageAccountCode ?? UNMAPPED_LABEL,
        account.dealerCode ?? shortId(account.sellingDealerId),
        account.productLabel,
    ].join(' \u00b7 ');

export const postingsLabel = (label: string, postings: number): string =>
    `${label} · ${postings} ${postings === 1 ? 'posting' : 'postings'}`;

export const fundAccountSubtitle = (dealerCode: string | null, sellingDealerId: string, productLabel: string): string =>
    `Client Fund Account for ${dealerCode ?? shortId(sellingDealerId)}, ${productLabel}`;

export const INVALID_FUND_ACCOUNT_LINK_MESSAGE = 'This fund account link is not valid.';

export const lineTotalLabel = (label: string): string => `Total for ${label}`;

export const POSTINGS_MISMATCH_MESSAGE =
    'These rows no longer add up to the line above; the statement has moved. Reload to refresh both.';

export const postingsUnavailableMessage = (label: string): string =>
    `The ledger is unavailable, so the postings for ${label} could not be loaded.`;

export const SUMMARIES_UNAVAILABLE_MESSAGE =
    'Agreement details could not be loaded, so the registration, customer and sale columns are blank.';

export const BLOCK_TITLES: Record<StatementBlockKey, string> = {
    motorplan_handled: 'Funds handled by MotorPlan',
    dealer_handled: 'Funds handled by the merchant',
    internal: 'Funds moved internally',
    paid_out: 'Funds paid out',
};

export const BLOCK_EMPTY_MESSAGES: Record<StatementBlockKey, string> = {
    motorplan_handled: 'No amounts were handled by MotorPlan in this period.',
    dealer_handled: 'No amounts were handled by the merchant in this period.',
    internal: 'No amounts were moved internally in this period.',
    paid_out: 'No amounts were paid out in this period.',
};

export const UNSPLIT_LABEL = 'unsplit';
export const UNASSIGNED_LABEL = 'of which, Unassigned product (gross)';
export const GROSS_BALANCE_LABEL = 'Gross, as verified by the ledger';
export const NOT_RECORDED_LABEL = 'Not recorded';
export const NET_BASIS_FOOTNOTE =
    'Net figures are the gross less the VAT recorded on each posting when it was made; the ledger verifies the gross.';
export const MOTORPLAN_JOURNAL_NOTE = "Journal this block's VAT from these figures.";

export const unsplitBadgeLabel = (postings: number): string => `${postings} ${UNSPLIT_LABEL}`;

const postingCount = (postings: number): string => `${postings} ${postings === 1 ? 'posting' : 'postings'}`;

export const blockUnsplitNotice = (split: boolean, postings: number, gross: string, currency: string): string =>
    `The ${split ? 'VAT and Net totals exclude' : 'Net total excludes'} ${postingCount(postings)} (${formatVerbatimMoneyCurrency(gross, currency)} gross) whose split is not yet recorded. This notice clears when every posting in the period is split.`;

export const balanceUnsplitNotice = (postings: number, gross: string, currency: string): string =>
    `The net balances include ${postingCount(postings)} (${formatVerbatimMoneyCurrency(gross, currency)} gross) at their gross because their split is not yet recorded.`;

export const postingsRowsLabel = (postings: number, rows: number): string =>
    `${postingCount(postings)} on this page, shown as ${rows} ${rows === 1 ? 'row' : 'rows'}`;
